import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import LegacyLandingDisplay from '@/components/b2b/Landing/LegacyLandingDisplay';
import b2bMessages from '@/components/b2b/shared/messages';
import Loader from '@/components/Loader';
import { isAuthPrompt, useAuthPrompt } from '@/context/auth/AuthPromptProvider';
import { useAnalytics } from '@/hooks/analytics';
import { useUserState } from '@/hooks/store';
import { setPartner } from '@/store/partner/actions';
import { Subscription, User } from '@/store/user/types';
import { calmLogger } from '@/utils/calmLogger';
import { getCookie, removeCookie } from '@/utils/cookies';
import { getPartnerInfo, putPartnerSubscribe } from '@/utils/endpoints';
import { postRequestAccess } from '@/utils/endpoints/postRequestAccess';
import getParamAsString from '@/utils/getParamsAsString';
import {
	hasSubscriptionConflictingWithB2BRedemption,
	isB2BSubscriptionWithPartner,
	isExpired,
	shouldShowRenewal,
} from '@/utils/subscriptions';

import { isEligibilityErrorKey } from '../Eligibility/types';
import { isPartner, partners } from '../shared/data';
import {
	IntegrationType,
	isSignupScreenKey,
	LandingDisplayProps,
	LandingProps,
	OnB2BScreenChange,
	PartnerCategory,
	PartnerData,
	PartnerDetails,
	SignupScreensKey,
} from '../shared/types';
import { signupScreens } from './signupScreens';
import { LoaderContainer } from './styles';

const Landing = ({ error, partnerSlug, referer, linkToken, rawPartner }: LandingProps) => {
	const user = useUserState();
	const { logEvent } = useAnalytics();
	const [errorData, setErrorData] = useState(error);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const defaultPartnerData = partners.default;
	const [partnerDetails, setPartnerDetails] = useState<PartnerData>({
		id: '',
		name: '',
		slug: '',
		requires_data_sharing: false,
		supports_eligibility_list: false,
		integration_type: IntegrationType.ELIGIBILITY_FILE,
		pageInfo: defaultPartnerData,
		should_show_request_access_flow: false,
		redemption_url: 'https://redemption-url.com',
	});
	const [activeScreen, setActiveScreen] = useState<SignupScreensKey | null>(null);
	const dispatch = useDispatch();
	const { setAuthPrompt } = useAuthPrompt();
	const searchParams = useSearchParams();
	const supportUrl = useState(() => getParamAsString(searchParams?.get('support_url')))[0];
	const redirectPath = useState(() => getParamAsString(searchParams?.get('redirect_path')))[0];
	const context = useState(() => getParamAsString(searchParams?.get('context')))[0];
	const redirectURI = useState(() => getParamAsString(searchParams?.get('redirect_uri')))[0];

	const b2bEwbGroupCodesEnabled = true;
	const shouldShowRevampedRedemptionFlow = true;
	const groupCode = useState(() => getParamAsString(searchParams?.get('code')))[0];
	const [queryGroupCodeError, setQueryGroupCodeError] = useState<boolean>(false);

	const prefilledPartnerUserId = useState(() => {
		const ddId = getParamAsString(searchParams?.get('ddSubscriptionId'));
		if (ddId) {
			return ddId;
		}
		return getParamAsString(searchParams?.get('partner_user_id'));
	})[0];
	const prefilledEncryptedPartnerUserId = useState(() => {
		return getParamAsString(searchParams?.get('puid_token'));
	})[0];
	const dontClearCookies = useState(() =>
		// We (almost always) automatically log the user out when visiting this page, unless this query param is present
		getParamAsString(searchParams?.get('preserveSession')),
	)[0];
	const takeToAuthMode = useState(() => {
		const queryAuthMode = getParamAsString(searchParams?.get('authMode'));
		if (queryAuthMode && isAuthPrompt(queryAuthMode)) {
			return queryAuthMode;
		}
		return null;
	})[0];

	const [userAccessCode, setUserAccessCode] = useState('');

	const onScreenChange: OnB2BScreenChange = (screen, mode) => {
		if (screen === 'account') {
			if (mode) {
				setAuthPrompt(mode);
			}
			setActiveScreen(screen);
		} else {
			setActiveScreen(screen);
		}
	};

	const redeem: LandingDisplayProps['redeem'] = async (_partnerDetails, uniqueId) => {
		setIsSubmitting(true);
		const { requires_data_sharing, id, pageInfo } = _partnerDetails;
		const dataSharingOptIn = requires_data_sharing;
		const redemptionMethod = (() => {
			if (linkToken) {
				return 'URL: calm-link-token';
			}
			if (prefilledPartnerUserId) {
				return 'URL: partner_user_id';
			}
			if (prefilledEncryptedPartnerUserId) {
				return 'URL: puid_token';
			}
			return 'Manual entry';
		})();
		const eventProps = {
			method: redemptionMethod,
			partner_id: id,
			partner_type: partnerDetails?.category?.toLowerCase() || null,
		};
		try {
			setErrorData(undefined);
			const partnerInfo = uniqueId
				? {
						partnerUserId: uniqueId.partner_user_id,
						puidToken: uniqueId.puid_token,
						partnerId: id,
						dataSharingOptIn,
						groupCode: uniqueId.group_code,
					}
				: { linkToken, dataSharingOptIn };
			logEvent({
				eventName: 'B2B Redemption : Submitted',
				eventProps,
			});
			if (partnerInfo.partnerUserId) setUserAccessCode(partnerInfo.partnerUserId);
			await putPartnerSubscribe(partnerInfo);
			setIsSubmitting(false);

			logEvent({
				eventName: 'B2B Redemption : Success',
				eventProps,
			});
			removeCookie('calm-link-token');
			if (redirectPath) {
				// redirect to a web-www redirect_path param
				// eslint-disable-next-line @next/next/no-location-assign-relative-destination
				window.location.href = `${window.location.origin}/${redirectPath}`;
			} else if (redirectURI) {
				// redirect to a url specified in redirect_uri param
				window.location.href = redirectURI;
			} else if (pageInfo.successRedirectUrl) {
				// redirect to a static url specified in the partner config
				window.location.href = pageInfo.successRedirectUrl;
			} else if (context === 'pathwaysAnnouncement') {
				onScreenChange('pathwaysSuccess');
			} else {
				onScreenChange('success');
			}
		} catch (err) {
			setIsSubmitting(false);
			logEvent({
				eventName: 'B2B Redemption : Failure',
				eventProps,
			});
			if (err?.data.error.code === 'b2b_user_has_existing_subscription') {
				onScreenChange('sorry');
			} else if (
				b2bEwbGroupCodesEnabled &&
				partnerDetails.integration_type === IntegrationType.GROUP_CODE &&
				groupCode &&
				(err?.data.error.code === 'b2b_partner_group_code_not_found' ||
					err?.data.error.code === 'b2b_partner_group_code_mismatch') &&
				!queryGroupCodeError
			) {
				setQueryGroupCodeError(true);
				onScreenChange('eligibility');
			} else {
				setErrorData(err?.data.error);
			}
		}
	};

	function requestAccessCall(id: string, firstName: string, lastName: string, email: string): void {
		postRequestAccess(id, firstName, lastName, email)
			.then(() => {
				logEvent({
					eventName: 'Access : Success',
					eventProps: {
						partner_id: partnerDetails.id as string,
					},
				});
				onScreenChange('accessSuccess');
			})
			.catch(e => {
				setErrorData(e.data.error);
			});
	}

	async function onAuthSuccess(_user: User | null, nextPartnerDetails?: PartnerData): Promise<void> {
		const _partnerDetails = nextPartnerDetails ?? partnerDetails;
		if (linkToken) {
			await redeem(_partnerDetails);
		} else if (prefilledPartnerUserId) {
			await redeem(_partnerDetails, {
				partner_user_id: prefilledPartnerUserId,
			});
		} else if (prefilledEncryptedPartnerUserId) {
			await redeem(_partnerDetails, {
				puid_token: prefilledEncryptedPartnerUserId,
			});
		} else if (_partnerDetails) {
			const canRedeemWithoutEligibility = _partnerDetails.category === PartnerCategory.SALES_TRIAL;
			if (canRedeemWithoutEligibility && _user?.email) {
				await redeem(_partnerDetails, {
					partner_user_id: _user?.email,
				});
			} else if (
				b2bEwbGroupCodesEnabled &&
				partnerDetails.integration_type === IntegrationType.GROUP_CODE &&
				groupCode
			) {
				await redeem(_partnerDetails, { group_code: decodeURIComponent(groupCode) });
			} else {
				onScreenChange('eligibility');
			}
		} else {
			// This will almost certainly fail, but it'll take the user to the correct error screen
			await redeem(_partnerDetails);
		}
	}

	// Landed on the page without a link token (required for sso), and partner doesn't support eligibility list
	function isInvalidSso(integrationType: IntegrationType, supportsEligibilityList: boolean): boolean {
		return integrationType === IntegrationType.SSO && !supportsEligibilityList && !linkToken;
	}

	function shouldAutoRedirectToRenew(_partnerDetails: PartnerDetails, subscription?: Subscription): boolean {
		// If they don't have a B2B sub with this specific partner, they couldn't possibly renew
		if (!isB2BSubscriptionWithPartner(_partnerDetails.id, subscription)) {
			return false;
		}
		if (isExpired(subscription)) {
			// If the user is expired, only forcibly redirect to the renewal page if it offers a B2B renewal option
			// Otherwise, let them redeem a new partner_user_id with this same partner
			return Boolean(_partnerDetails.web_renew_url);
		}
		// If they're not yet expired, allow them to renew before they expire (if it's not too far away)
		return shouldShowRenewal(subscription);
	}

	async function fetchPartnerInfo(): Promise<void> {
		const userToken = getCookie('calm-user-token');
		if (partnerSlug === 'zoom') {
			setPartnerDetails({
				id: 'zoom',
				name: 'zoom',
				requires_data_sharing: false,
				slug: '/zoom/app,',
				supports_eligibility_list: false,
				integration_type: 'ELIGIBILITY_FILE',
				pageInfo: partners.zoom,
				redemption_url: 'https://redemption-url.com',
			});
			return;
		}
		try {
			const partnerPageInfo = isPartner(partnerSlug) ? partners[partnerSlug] : partners.default;
			let partnerApiResponse;

			// moved the API call to the server side render to allow routing between DTC and B2B. Added this conditional to not make
			// another call if the rawPartner response is passed in.
			if (rawPartner) {
				partnerApiResponse = rawPartner;
			} else {
				const { data } = await getPartnerInfo({ slug: partnerSlug.toString() });
				partnerApiResponse = data.partner;
			}

			const _partnerData = {
				pageInfo: partnerPageInfo,
				...partnerApiResponse,
			};
			if (!isPartner(partnerSlug)) {
				_partnerData.pageInfo.messages.activate = b2bMessages.freeOfferMessage;
			}
			setPartnerDetails(_partnerData);
			dispatch(setPartner(_partnerData));

			// Go straight to the error page if we've been passed error as a prop
			if (error) {
				return;
			}

			if (isInvalidSso(partnerApiResponse?.integration_type, partnerApiResponse?.supports_eligibility_list)) {
				setErrorData({ code: 'sso_redirect' });
				return;
			}

			const preserveUserSession = userToken && dontClearCookies;
			const isValidLinkToken = linkToken && linkToken !== 'null';
			const isValidRenewingUser = userToken && referer === 'renew' && isValidLinkToken;

			if (preserveUserSession || isValidRenewingUser) {
				// we have to pass _partnerData here as the new values are not yet in scope of the onAuthSuccess function
				await onAuthSuccess(user, _partnerData);
				return;
			} else {
				removeCookie('calm-user-token');
				if (takeToAuthMode) {
					onScreenChange('account', takeToAuthMode);
				} else if (shouldShowRevampedRedemptionFlow) {
					onScreenChange('account');
				} else {
					onScreenChange('welcome');
				}
			}

			// Show renewal page
			if (referer === 'renew' || shouldAutoRedirectToRenew(_partnerData, user?.subscription)) {
				onScreenChange('renew');
				return;
			}
			if (
				// user has active subscription take them directly to sorry
				partnerApiResponse.supports_eligibility_list &&
				userToken &&
				hasSubscriptionConflictingWithB2BRedemption(user?.subscription)
			) {
				onScreenChange('sorry');
				return;
			}
		} catch (err) {
			// Go straight to the error page if we've been passed error as a prop
			if (error) {
				return;
			}
			setErrorData(err?.data?.error);
		}
	}

	useEffect(
		() => {
			if (errorData?.code && !(activeScreen === 'eligibility' && isEligibilityErrorKey(errorData.code))) {
				onScreenChange('error');
			}
		}, // TODO [WEB-1595]: Fix this exhaustive-deps issue
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[errorData],
	);

	useEffect(
		() => {
			// remove the calm-link-token query param
			if (referer !== 'error') {
				const url = new URL(window.location.href);
				url.searchParams.delete('calm-link-token');
				window.history.replaceState({}, '', url.toString());
			}
			fetchPartnerInfo().catch(error => calmLogger.error('Error in b2b Landing fetchPartnerInfo', {}, error));
			if (error) {
				onScreenChange('error');
			}
		}, // TODO [WEB-1595]: Fix this exhaustive-deps issue
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[error],
	);

	const Component =
		activeScreen && isSignupScreenKey(activeScreen) ? signupScreens[activeScreen].Component : null;
	if (activeScreen && Component) {
		return (
			<LegacyLandingDisplay
				Component={Component}
				activeScreen={activeScreen}
				errorData={errorData}
				isSubmitting={isSubmitting}
				onAuthSuccess={onAuthSuccess}
				onScreenChange={onScreenChange}
				partnerDetails={partnerDetails}
				redeem={redeem}
				requestAccessCall={requestAccessCall}
				userAccessCode={userAccessCode}
				supportUrl={supportUrl}
				name={signupScreens[activeScreen].name}
				isRevampRedempFlow={shouldShowRevampedRedemptionFlow}
			/>
		);
	}
	return (
		<LoaderContainer>
			<Loader color="black" />
		</LoaderContainer>
	);
};

export default Landing;
